import { z } from "zod";
/**
 * Bulk WhatsApp template sends ("envíos masivos").
 *
 * A Pattern-B schema like `createTemplateSchema`: `safeParse`d in the browser to
 * render inline errors AND attached as the Fastify body schema, so the two
 * cannot drift.
 */
/**
 * The hard ceiling on one send.
 *
 * Evaluated on MATCHED leads, before exclusions — the simpler rule, and the one
 * the disabled button's copy can state honestly ("los filtros seleccionan N").
 * The CRM disabling the button is UX; the endpoint refuses independently.
 */
export const BROADCAST_MAX_RECIPIENTS = 500;
/**
 * Lead fields a template variable may be mapped to.
 *
 * ONE list, imported by the backend resolver and the CRM picker, so a field the
 * form offers is always a field the resolver knows.
 *
 * Deliberately NOT here, and each for its own reason: `phone` and `email` (PII
 * echoed back at the person with no use case), `score` (an internal number
 * leaking to a prospect), and `capturedFields.*` — free text a language model
 * wrote from the prospect's OWN words, so mapping it into a template parameter
 * is a prompt-injection payload with a delivery mechanism to 500 people. This
 * comment exists because that last one is exactly the field someone adds later
 * "because it's there".
 */
export const BROADCAST_LEAD_FIELDS = [
    // Earns its own key: "Hola {{nombre}}" against "María Fernanda Gutiérrez
    // López" reads like a letter from a bank, and `leads.name` in the pilot is
    // frequently the full legal name.
    { key: "firstName", label: "Primer nombre" },
    { key: "name", label: "Nombre completo" },
    { key: "productName", label: "Programa de interés" },
    { key: "cycleLabel", label: "Ciclo" },
    { key: "sessionRef", label: "Referencia (ES-042)" },
    { key: "stageLabel", label: "Etapa" },
];
export const broadcastLeadFieldSchema = z.enum([
    "firstName",
    "name",
    "productName",
    "cycleLabel",
    "sessionRef",
    "stageLabel",
]);
/**
 * What one `{{variable}}` is filled with.
 *
 * A `literal` can never be missing — `min(1)` below — which is why the
 * `missing_variable` exclusion only ever fires on a `lead_field`.
 */
export const broadcastVariableBindingSchema = z.discriminatedUnion("source", [
    z.object({ source: z.literal("lead_field"), field: broadcastLeadFieldSchema }),
    z.object({
        source: z.literal("literal"),
        value: z
            .string()
            .trim()
            .min(1, "Escribe el texto que recibirán todos")
            .max(120, "Máximo 120 caracteres"),
    }),
]);
/** Keyed by the `{{token}}` exactly as `extractVariables` returns it. */
export const broadcastVariableMappingSchema = z.record(z.string(), broadcastVariableBindingSchema);
/**
 * The filter set that decides who receives the message.
 *
 * A mirror of the pipeline's `leadFilterSchema` in the JSON body shape rather
 * than the querystring one. `stages` is present and load-bearing: the Kanban
 * board omits it from its own query and hides columns client-side instead, so a
 * broadcast built from what the board fetched would go to the entire pipeline
 * when the advisor had narrowed it to some columns.
 *
 * Every list is OR'd inside itself and AND'd with the rest; `"none"` in
 * `productIds` / `cycleIds` is the "no program" / "no cycle" bucket.
 *
 * `.strict()` because this is the money path: a filter key the server does not
 * know (a tab still running an older CRM bundle that sends `productId`) must be
 * a 400, never silently stripped into a send to the whole pipeline.
 */
export const broadcastFiltersSchema = z
    .object({
    stages: z.array(z.string().min(1).max(64)).max(50).optional(),
    channel: z.enum(["widget", "whatsapp"]).optional(),
    scoreMin: z.number().int().min(0).max(100).optional(),
    scoreMax: z.number().int().min(0).max(100).optional(),
    productIds: z.array(z.union([z.string().uuid(), z.literal("none")])).max(50).optional(),
    cycleIds: z.array(z.union([z.string().uuid(), z.literal("none")])).max(50).optional(),
    search: z.string().max(100).optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    goneCold: z.boolean().optional(),
    labelIds: z.array(z.string().uuid()).max(50).optional(),
    // Team member responsible for the lead; "none" = unassigned.
    assigneeIds: z.array(z.union([z.string().uuid(), z.literal("none")])).max(50).optional(),
})
    .strict();
export const createBroadcastSchema = z.object({
    templateId: z.string().uuid(),
    filters: broadcastFiltersSchema,
    mapping: broadcastVariableMappingSchema.default({}),
    /**
     * The file for THIS send, when the template has a media header.
     *
     * ONE file for the whole broadcast, never one per lead. That is not a
     * simplification: a per-lead file would have to be sourced from lead data,
     * which is exactly what the deny-list above refuses for text parameters, for
     * the same reason. Absent ⇒ the template's own sample file is used.
     */
    headerMediaAssetId: z.string().uuid().nullable().optional(),
    /**
     * What WhatsApp shows the recipient on a PDF.
     *
     * Editable per send because "folleto_v3_final (1).pdf" is what comes off a
     * computer, and it is the prospect who reads it. Absent ⇒ the file's own name.
     */
    headerMediaFileName: z
        .string()
        .trim()
        .min(1)
        .max(255, "Máximo 255 caracteres")
        // A path separator here would be Meta's problem to reject and ours to have
        // sent; the recipient sees a name, not a location.
        .refine((v) => !v.includes("/") && !v.includes("\\"), "El nombre no puede incluir / ni \\")
        .nullable()
        .optional(),
});
export const listBroadcastsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const listBroadcastRecipientsQuerySchema = z.object({
    status: z.string().max(24).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const setBroadcastAuthorizationSchema = z.object({ enabled: z.boolean() });
/** Why a matched lead is not receiving the message. Order = UI listing order. */
export const BROADCAST_EXCLUSION_REASONS = [
    "no_phone",
    "opted_out",
    "blacklisted",
    "duplicate_phone",
    "missing_variable",
    "cancelled",
];
export const BROADCAST_EXCLUSION_LABELS = {
    no_phone: "Sin número de WhatsApp",
    opted_out: "Pidieron no recibir mensajes",
    blacklisted: "En la lista de bloqueo",
    duplicate_phone: "Número repetido en este envío",
    missing_variable: "Faltan datos para personalizar el mensaje",
    cancelled: "El envío se detuvo antes de llegar a ellos",
};
export const BROADCAST_STATUSES = [
    "queued",
    "sending",
    "completed",
    "cancelled",
    "failed",
];
export const BROADCAST_RECIPIENT_STATUSES = [
    "pending",
    "sending",
    "sent",
    "delivered",
    "read",
    "failed",
    "excluded",
    "unknown",
];
/**
 * How far along a recipient is, for the "a late write must not downgrade" guard.
 *
 * Meta can deliver a `delivered` status before our own send loop has finished
 * writing `sent`, so every status write compares ranks first.
 */
const RECIPIENT_STATUS_RANK = {
    pending: 0,
    sending: 1,
    sent: 2,
    delivered: 3,
    read: 4,
    // Terminal states sit above the delivery ladder: nothing may move a recipient
    // out of them.
    failed: 5,
    excluded: 5,
    unknown: 5,
};
export function recipientStatusRank(status) {
    return RECIPIENT_STATUS_RANK[status] ?? 0;
}
//# sourceMappingURL=whatsapp-broadcasts.js.map